import React, { createContext, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { digitalSignature } from 'app/shared/digital-signature/digital-signature.service';
import { AKD, FINA } from 'app/shared/digital-signature/digital-signature.model';
import DigitalSignatureModal from 'app/shared/digital-signature/digital-signature-modal';
import { showToast } from 'app/shared/util/toast.util';
import { useLocation } from 'react-router-dom';
import { LOCAL_STORAGE_KEYS } from 'app/shared/util/local-storage-keys';

type OpenArgs = { entity: string; idEntity: number };
type Ctx = {
  openDigitalSignatureModal: (args: OpenArgs) => void;
  close: () => void;
};

const Ctx = createContext<Ctx | null>(null);

export function useDigitalSignatureModal() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDigitalSignatureModal must be used within DigitalSignatureModalProvider');
  return ctx;
}

function currentUrlWithoutAnyParams(): string {
  const { origin, pathname, hash } = window.location;
  return `${origin}${pathname}${hash ?? ''}`;
}

export const DigitalSignatureModalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isOpen, setOpen] = useState(false);
  const [entity, setEntity] = useState<string | null>(null);
  const [idEntity, setIdEntity] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const location = useLocation();

  const openDigitalSignatureModal = ({ entity, idEntity }: OpenArgs) => {
    setEntity(entity);
    setIdEntity(idEntity);
    setOpen(true);
  };
  const close = () => setOpen(false);

  async function digSign(signatureEnum: number) {
    if (!entity || idEntity == null) return;
    try {
      setLoading(true);

      if (location.state?.from !== undefined) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.AMENDMENT_FROM, location.state.from)
      }

      const currentUrl = currentUrlWithoutAnyParams();
      const response = await digitalSignature(entity, idEntity, signatureEnum, currentUrl);
      if (response?.tokenUrl) {
        window.location.href = response.tokenUrl.toString();
      } else if (response?.error) {
        showToast(toast.error, response.error);
      }
    } finally {
      setLoading(false);
      setOpen(false);
    }
  }

  const ctxValue = useMemo(() => ({ openDigitalSignatureModal, close }), []);

  return (
    <Ctx.Provider value={ctxValue}>
      {children}
      <DigitalSignatureModal
        isModalOpen={isOpen}
        setModalOpen={setOpen}
        signLoading={loading}
        onSignAKD={() => digSign(AKD)}
        onSignFINA={() => digSign(FINA)}
      />
    </Ctx.Provider>
  );
};
